<!-- ============================================ -->
<!-- event-create.component.html -->
<!-- ============================================ -->

@if (!isLoading) {
  <div class="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <button
          (click)="cancel()"
          class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white dark:text-white flex items-center gap-2 mb-4"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Retour
        </button>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
          {{ isEditMode ? "✏️ Modifier l'événement" : "✨ Nouvel événement" }}
        </h1>
        <p class="text-gray-600 dark:text-gray-400 mt-2">
          {{
            isEditMode
              ? "Modifie les informations de ton événement"
              : "Crée ton événement et obtiens instantanément des recommandations personnalisées"
          }}
        </p>
      </div>
      <!-- Error Message -->
      @if (errorMessage) {
        <div
          class="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 mb-6 rounded"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-red-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-700 dark:text-red-300">
                {{ errorMessage }}
              </p>
              @if (limitReached) {
                <button
                  type="button"
                  (click)="goToPlans()"
                  class="mt-2 text-sm font-semibold text-purple-700 dark:text-purple-300 underline"
                >
                  Voir les plans
                </button>
              }
            </div>
          </div>
        </div>
      }
      <!-- Formulaire -->
      <form
        [formGroup]="eventForm"
        (ngSubmit)="onSubmit()"
        class="bg-white dark:bg-slate-800 rounded-2xl shadow-lg dark:shadow-slate-900/50 p-8"
      >
        <!-- Section 1: Informations générales -->
        <div class="mb-8">
          <h2
            class="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2"
          >
            <span
              class="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm"
              >1</span
            >
            Informations générales
          </h2>
          <!-- Nom événement -->
          <div class="mb-6">
            <label
              for="name"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Nom de l'événement <span class="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              formControlName="name"
              placeholder="Ex: Marché de Noël Antibes"
              class="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition"
              [class.border-red-500]="
                eventForm.get('name')?.invalid && eventForm.get('name')?.touched
              "
            />
            @if (
              eventForm.get("name")?.invalid && eventForm.get("name")?.touched
            ) {
              <div class="mt-2">
                @if (eventForm.get("name")?.errors?.["required"]) {
                  <p class="text-sm text-red-600 dark:text-red-400">
                    Le nom est obligatoire
                  </p>
                }
                @if (eventForm.get("name")?.errors?.["minlength"]) {
                  <p class="text-sm text-red-600 dark:text-red-400">
                    Le nom doit contenir au moins 3 caractères
                  </p>
                }
                @if (eventForm.get("name")?.errors?.["maxlength"]) {
                  <p class="text-sm text-red-600 dark:text-red-400">
                    Le nom ne peut pas dépasser 100 caractères
                  </p>
                }
              </div>
            }
          </div>
          <!-- Date -->
          <div class="mb-6">
            <label
              for="eventDate"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              📅 Date <span class="text-red-500">*</span>
            </label>
            <input
              id="eventDate"
              type="date"
              formControlName="eventDate"
              [min]="minDate"
              [max]="maxDate"
              class="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition"
              [class.border-red-500]="
                eventForm.get('eventDate')?.invalid &&
                eventForm.get('eventDate')?.touched
              "
            />
            @if (
              eventForm.get("eventDate")?.touched &&
              eventForm.get("eventDate")?.errors
            ) {
              <div class="mt-2">
                @if (eventForm.get("eventDate")?.errors?.["required"]) {
                  <p class="text-sm text-red-600 dark:text-red-400">
                    La date est obligatoire
                  </p>
                }
                @if (eventForm.get("eventDate")?.errors?.["pastDate"]) {
                  <p class="text-sm text-red-600 dark:text-red-400">
                    {{ eventForm.get("eventDate")?.errors?.["pastDate"] }}
                  </p>
                }
              </div>
            }
          </div>
          <!-- Horaires -->
          <div class="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <label
                for="startTime"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                ⏰ Heure de début <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <button
                  type="button"
                  class="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg text-left flex items-center justify-between"
                  (click)="openStartTime($event)"
                  [class.border-red-500]="
                    eventForm.get('startTime')?.invalid &&
                    eventForm.get('startTime')?.touched
                  "
                >
                  <span>{{
                    eventForm.get("startTime")?.value || "-- Choisir --"
                  }}</span>
                  <svg
                    class="w-4 h-4 transform transition-transform"
                    [ngClass]="{ 'rotate-180': startTimeOpen }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                @if (startTimeOpen) {
                  <div
                    (click)="$event.stopPropagation()"
                    class="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border rounded shadow max-h-40 overflow-auto"
                  >
                    @for (slot of timeSlots; track slot) {
                      <button
                        [id]="'start-' + slot"
                        type="button"
                        (click)="selectTime('startTime', slot)"
                        class="w-full text-left px-3 py-2 hover:bg-indigo-100 dark:bg-indigo-900/30"
                        [class.bg-indigo-50]="
                          slot === '09:00' && !eventForm.get('startTime')?.value
                        "
                        [class.font-semibold]="
                          slot === '09:00' && !eventForm.get('startTime')?.value
                        "
                      >
                        {{ slot }}
                      </button>
                    }
                  </div>
                }
              </div>
            </div>
            <div>
              <label
                for="endTime"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                ⏰ Heure de fin <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <button
                  type="button"
                  class="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg text-left flex items-center justify-between"
                  (click)="openEndTime($event)"
                  [class.border-red-500]="
                    eventForm.get('endTime')?.invalid &&
                    eventForm.get('endTime')?.touched
                  "
                >
                  <span>{{
                    eventForm.get("endTime")?.value || "-- Choisir --"
                  }}</span>
                  <svg
                    class="w-4 h-4 transform transition-transform"
                    [ngClass]="{ 'rotate-180': endTimeOpen }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                @if (endTimeOpen) {
                  <div
                    (click)="$event.stopPropagation()"
                    class="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border rounded shadow max-h-40 overflow-auto"
                  >
                    @for (slot of timeSlots; track slot) {
                      <button
                        [id]="'end-' + slot"
                        type="button"
                        (click)="selectTime('endTime', slot)"
                        class="w-full text-left px-3 py-2 hover:bg-indigo-100 dark:bg-indigo-900/30"
                        [class.bg-indigo-50]="
                          slot === '17:00' && !eventForm.get('endTime')?.value
                        "
                        [class.font-semibold]="
                          slot === '17:00' && !eventForm.get('endTime')?.value
                        "
                      >
                        {{ slot }}
                      </button>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
          <!-- Durée calculée -->
          @if (calculatedDuration) {
            <div class="mb-6">
              <div
                class="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 flex items-center gap-3"
              >
                <svg
                  class="w-5 h-5 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span
                  class="text-sm font-medium text-purple-900 dark:text-purple-300"
                  >{{ calculatedDuration }}</span
                >
              </div>
            </div>
          }
          <!-- Erreurs horaires -->
          @if (
            eventForm.errors?.["invalidTime"] &&
            (eventForm.get("startTime")?.touched ||
              eventForm.get("endTime")?.touched)
          ) {
            <div class="mb-6">
              <p class="text-sm text-red-600 dark:text-red-400">
                {{ eventForm.errors?.["invalidTime"] }}
              </p>
            </div>
          }
          @if (eventForm.errors?.["minDuration"]) {
            <div class="mb-6">
              <p class="text-sm text-red-600 dark:text-red-400">
                {{ eventForm.errors?.["minDuration"] }}
              </p>
            </div>
          }
          @if (eventForm.errors?.["maxDuration"]) {
            <div class="mb-6">
              <p class="text-sm text-red-600 dark:text-red-400">
                {{ eventForm.errors?.["maxDuration"] }}
              </p>
            </div>
          }
          <!-- Lieu (LocationPicker avec carte et rayon d'action) -->
          <div class="mb-6">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              📍 Lieu <span class="text-red-500">*</span>
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Cherchez et sélectionnez l'adresse précise de votre événement. Le
              cercle indique votre rayon d'action.
            </p>
            <app-location-picker
              [baseAddress]="userPreselectedAddress || undefined"
              [preselectedAddress]="duplicatedEventAddress || undefined"
              [actionRadius]="userActionRadius"
              [mapHeight]="'640px'"
              (addressSelected)="onAddressSelected($event)"
              (routeUpdated)="onRouteUpdated($event)"
            ></app-location-picker>
            @if (
              eventForm.get("location")?.invalid &&
              eventForm.get("location")?.touched
            ) {
              <div class="mt-2">
                <p class="text-sm text-red-600 dark:text-red-400">
                  Le lieu est obligatoire — sélectionnez une adresse dans la
                  liste
                </p>
              </div>
            }
          </div>
        </div>
        <!-- Section 2: Type et affluence -->
        <div class="mb-8">
          <h2
            class="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2"
          >
            <span
              class="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm"
              >2</span
            >
            Type et affluence
          </h2>
          <!-- Type événement -->
          <div class="mb-6">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3"
            >
              🎯 Type d'événement <span class="text-red-500">*</span>
            </label>
            <div class="grid md:grid-cols-2 gap-3">
              @for (type of eventTypes; track type) {
                <div
                  (click)="eventForm.patchValue({ eventType: type.value })"
                  class="relative border-2 rounded-lg p-4 cursor-pointer transition hover:border-purple-600 hover:bg-purple-50 dark:bg-purple-900/20"
                  [class.border-purple-600]="
                    eventForm.get('eventType')?.value === type.value
                  "
                  [class.bg-purple-50]="
                    eventForm.get('eventType')?.value === type.value
                  "
                  [class.border-gray-200]="
                    eventForm.get('eventType')?.value !== type.value
                  "
                >
                  <!-- Radio button visuel -->
                  <div class="flex items-start gap-3">
                    <div class="flex-shrink-0 mt-1">
                      <div
                        class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition"
                        [class.border-purple-600]="
                          eventForm.get('eventType')?.value === type.value
                        "
                        [class.bg-purple-600]="
                          eventForm.get('eventType')?.value === type.value
                        "
                        [class.border-gray-300]="
                          eventForm.get('eventType')?.value !== type.value
                        "
                      >
                        @if (eventForm.get("eventType")?.value === type.value) {
                          <div
                            class="w-2 h-2 bg-white dark:bg-slate-800 rounded-full"
                          ></div>
                        }
                      </div>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="text-xl">{{ type.icon }}</span>
                        <span
                          class="font-semibold text-gray-900 dark:text-white"
                          >{{ type.label }}</span
                        >
                      </div>
                      <p class="text-xs text-gray-600 dark:text-gray-400">
                        {{ type.description }}
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>
            @if (
              eventForm.get("eventType")?.invalid &&
              eventForm.get("eventType")?.touched
            ) {
              <div class="mt-2">
                <p class="text-sm text-red-600 dark:text-red-400">
                  Le type d'événement est obligatoire
                </p>
              </div>
            }
          </div>
          <!-- Affluence attendue -->
          <div class="mb-6">
            <label
              for="expectedAttendance"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              👥 Affluence attendue (optionnel)
            </label>
            <select
              id="expectedAttendance"
              formControlName="expectedAttendance"
              class="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition"
            >
              @for (level of attendanceLevels; track level) {
                <option [value]="level.value">
                  {{ level.label }}
                </option>
              }
            </select>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              💡 Améliore la précision si tu n'as pas d'historique similaire
            </p>
          </div>
          <!-- Récurrence -->
          <div class="mb-6">
            <label
              for="recurrence"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              🔄 Récurrence (optionnel)
            </label>
            <select
              id="recurrence"
              formControlName="recurrence"
              class="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition"
            >
              <option value="none">Événement unique</option>
              <option value="weekly">Toutes les semaines</option>
              <option value="biweekly">Toutes les 2 semaines</option>
              <option value="monthly">Tous les mois</option>
            </select>
            @if (eventForm.get("recurrence")?.value !== "none") {
              <p
                class="text-xs text-purple-600 dark:text-purple-400 mt-1 flex items-center gap-1"
              >
                ✨
                {{
                  eventForm.get("recurrence")?.value === "weekly"
                    ? "4 événements"
                    : eventForm.get("recurrence")?.value === "biweekly"
                      ? "4 événements"
                      : "3 événements"
                }}
                seront créés automatiquement à partir de la date choisie
              </p>
            }
          </div>
        </div>
        <!-- Section 3: Notes -->
        <div class="mb-8">
          <h2
            class="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2"
          >
            <span
              class="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm"
              >3</span
            >
            Notes (optionnel)
          </h2>
          <div class="mb-6">
            <label
              for="notes"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              📋 Informations complémentaires
            </label>
            <textarea
              id="notes"
              formControlName="notes"
              rows="4"
              placeholder="Ex: Public jeune 18-30 ans, beaucoup de bières attendues"
              class="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition resize-none"
              [class.border-red-500]="
                eventForm.get('notes')?.invalid &&
                eventForm.get('notes')?.touched
              "
            ></textarea>
            <div class="flex justify-between items-center mt-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">
                Ces notes sont pour ton usage personnel uniquement
              </p>
              <p
                class="text-xs"
                [class.text-gray-500]="
                  (eventForm.get('notes')?.value?.length || 0) <= 500
                "
                [class.text-red-600]="
                  (eventForm.get('notes')?.value?.length || 0) > 500
                "
              >
                {{ eventForm.get("notes")?.value?.length || 0 }}/500
              </p>
            </div>
          </div>
        </div>
        <!-- Actions -->
        <div class="flex flex-col sm:flex-row gap-4 pt-6 border-t">
          <button
            type="button"
            (click)="cancel()"
            class="flex-1 px-6 py-3 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-slate-600 dark:bg-slate-700 transition"
            [disabled]="isSaving"
          >
            Annuler
          </button>
          <button
            type="submit"
            class="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-bold hover:from-purple-700 hover:to-purple-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            [disabled]="isSaving || eventForm.invalid"
          >
            @if (isSaving) {
              <svg
                class="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            }
            @if (!isSaving && !isEditMode) {
              <span>🚀 Créer et voir les recommandations</span>
            }
            @if (!isSaving && isEditMode) {
              <span>💾 Enregistrer les modifications</span>
            }
            @if (isSaving) {
              <span>{{
                isEditMode
                  ? "Modification en cours..."
                  : "Génération en cours..."
              }}</span>
            }
          </button>
        </div>
        <!-- Info box -->
        <!-- 'Comment ça marche ?' section removed per request -->
      </form>
    </div>
  </div>
}

<!-- Conflict warning modal -->
@if (conflictWarning.length > 0) {
  <app-event-conflict-warning
    [conflicts]="conflictWarning"
    (confirm)="onConflictConfirm()"
    (cancel)="onConflictCancel()"
  ></app-event-conflict-warning>
}
